import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Tag, Statistic, Table, message, Spin, Progress, Input, Button } from 'antd';
import { StarOutlined, TrophyOutlined, ReloadOutlined, UserAddOutlined, WhatsAppOutlined, LineChartOutlined } from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const StatsAdoption = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/monitoring/stats-adoption');
      setData(r.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (d) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '-';
  const fmtDay = (d) => dayjs(d).format('DD/MM');

  const topTimesFiltrados = (data?.top_times || []).filter(t =>
    !busca || (t.game_name || '').toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}><StarOutlined /> Adoção de Avaliações & Estatísticas</Title>
          <Text type="secondary">
            Monitore adesão à feature: times com stats on, jogadores cadastrados, lançamentos, avaliações e broadcasts.
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
      </div>

      {loading && !data && <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>}

      {data && (
        <>
          {/* Adoção — % de times com stats_enabled */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={8}>
              <Card>
                <Statistic title="Adoção da feature" value={data.adocao.pct} suffix="%"
                  valueStyle={{ color: '#52c41a' }} />
                <Progress percent={data.adocao.pct} showInfo={false} strokeColor="#52c41a" style={{ marginTop: 8 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {data.adocao.ativos_com_stats} de {data.adocao.ativos} times ativos com <code>stats_enabled=true</code>
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={16}>
              <Card>
                <Row gutter={16}>
                  <Col xs={12} md={6}>
                    <Statistic title="Jogadores cadastrados" value={data.jogadores.total}
                      prefix={<UserAddOutlined />} valueStyle={{ color: '#1890ff' }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      +{data.jogadores.ult_24h} nas últimas 24h · +{data.jogadores.ult_7d} 7d
                    </Text>
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title="Cadastros via Google" value={data.jogadores.via_google} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {data.jogadores.total > 0 ? Math.round((data.jogadores.via_google / data.jogadores.total) * 100) : 0}% do total
                    </Text>
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title="Avaliadores únicos" value={data.ratings.avaliadores_unicos} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      telefones distintos que avaliaram
                    </Text>
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title="Nota média geral" value={data.ratings.media_geral || 0}
                      suffix="/ 5" valueStyle={{ color: '#faad14' }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      de {data.ratings.total} avaliações
                    </Text>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Volume — lançamentos, ratings, broadcasts, links */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Lançamentos (gols+assists)" value={data.lancamentos.total}
                  prefix={<TrophyOutlined />} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ⚽ {data.lancamentos.total_gols} gols · 🅰️ {data.lancamentos.total_assists} assists
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  +{data.lancamentos.ult_7d} nos últimos 7d
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Avaliações submetidas" value={data.ratings.total}
                  prefix={<StarOutlined />} valueStyle={{ color: '#faad14' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {data.ratings.jogos_avaliados} jogos · {data.ratings.times_avaliados} times
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  +{data.ratings.ult_7d} nos últimos 7d
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Rankings enviados no grupo"
                  value={data.broadcasts.ranking_do_jogo_total + data.broadcasts.ranking_anual_total + data.broadcasts.top_n_do_jogo_total}
                  prefix={<WhatsAppOutlined />} valueStyle={{ color: '#25D366' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Jogo: {data.broadcasts.ranking_do_jogo_total} · Anual: {data.broadcasts.ranking_anual_total} · Top-N: {data.broadcasts.top_n_do_jogo_total}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  +{data.broadcasts.ranking_do_jogo_7d + data.broadcasts.ranking_anual_7d + data.broadcasts.top_n_do_jogo_7d} nos últimos 7d
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Links 'Avaliar' enviados" value={data.links_avaliar.total} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {data.links_avaliar.times_ativados} times já receberam
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  +{data.links_avaliar.ult_24h} nas últimas 24h
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Funil de conversão do link avaliar */}
          <Card style={{ marginTop: 16 }} title={<><LineChartOutlined /> Funil da campanha de "Avaliar" (30 dias)</>}>
            <Row gutter={16}>
              <Col xs={12} md={6}>
                <Statistic title="Links enviados" value={data.funil_avaliar.links_enviados} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Visitas na LP /como-avaliar" value={data.funil_avaliar.visitas_lp}
                  suffix={<Text type="secondary" style={{ fontSize: 12 }}>({data.funil_avaliar.pct_visita}%)</Text>} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Cliques no CTA da LP" value={data.funil_avaliar.cliques_cta_lp}
                  suffix={<Text type="secondary" style={{ fontSize: 12 }}>({data.funil_avaliar.pct_clique}% das visitas)</Text>} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Cadastros novos" value={data.funil_avaliar.cadastros_jogadores}
                  valueStyle={{ color: '#1890ff' }}
                  suffix={<Text type="secondary" style={{ fontSize: 12 }}>({data.funil_avaliar.pct_cadastro_por_visita}% conv.)</Text>} />
              </Col>
            </Row>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              Nota: cadastros incluem jogadores que entraram por qualquer meio (não só via LP).
            </Text>
          </Card>

          {/* Série temporal */}
          <Card style={{ marginTop: 16 }} title="Evolução — últimos 30 dias">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dia" tickFormatter={fmtDay} />
                <YAxis />
                <RTooltip labelFormatter={fmtDay} />
                <Legend />
                <Line type="monotone" dataKey="jogadores" name="Cadastros jogadores" stroke="#1890ff" strokeWidth={2} />
                <Line type="monotone" dataKey="lancamentos" name="Lançamentos stats" stroke="#722ed1" strokeWidth={2} />
                <Line type="monotone" dataKey="ratings" name="Avaliações" stroke="#faad14" strokeWidth={2} />
                <Line type="monotone" dataKey="ranking_jogo" name="Ranking do jogo enviado" stroke="#25D366" strokeWidth={2} />
                <Line type="monotone" dataKey="ranking_anual" name="Ranking anual enviado" stroke="#13c2c2" strokeWidth={2} />
                <Line type="monotone" dataKey="links_avaliar" name="Link 'avaliar' enviado" stroke="#f5222d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Top times */}
          <Card
            style={{ marginTop: 16 }}
            title="Top times por engajamento na feature"
            extra={
              <Input.Search
                placeholder="Filtrar por nome do time"
                allowClear
                onChange={e => setBusca(e.target.value)}
                style={{ width: 260 }}
              />
            }
          >
            <Table
              size="small"
              rowKey="customer_id"
              dataSource={topTimesFiltrados}
              pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Time', dataIndex: 'game_name',
                  render: (v, r) => <span><b>{v}</b> <Tag color={r.status === 'ATIVO' ? 'green' : 'default'}>{r.status}</Tag>
                    {!r.stats_enabled && <Tag color="orange">stats OFF</Tag>}
                  </span>
                },
                { title: 'Lançamentos', dataIndex: 'lancamentos', width: 120, sorter: (a,b) => a.lancamentos - b.lancamentos },
                { title: 'Jogos c/ stats', dataIndex: 'jogos_lancados', width: 120, sorter: (a,b) => a.jogos_lancados - b.jogos_lancados },
                { title: 'Ratings recebidos', dataIndex: 'ratings_recebidos', width: 140, sorter: (a,b) => a.ratings_recebidos - b.ratings_recebidos, defaultSortOrder: 'descend' },
                { title: 'Ranking jogo enviado', dataIndex: 'ranking_jogo_enviados', width: 160, sorter: (a,b) => a.ranking_jogo_enviados - b.ranking_jogo_enviados },
                { title: 'Ranking anual enviado', dataIndex: 'ranking_anual_enviados', width: 160, sorter: (a,b) => a.ranking_anual_enviados - b.ranking_anual_enviados },
                { title: 'Última atividade', dataIndex: 'ultima_atividade', width: 160, render: fmtDate,
                  sorter: (a,b) => new Date(a.ultima_atividade).getTime() - new Date(b.ultima_atividade).getTime() },
              ]}
            />
          </Card>

          {/* Últimos jogadores cadastrados */}
          <Card style={{ marginTop: 16 }} title="Últimos jogadores cadastrados">
            <Table
              size="small"
              rowKey="id"
              dataSource={data.ultimos_jogadores}
              pagination={false}
              columns={[
                { title: '#', dataIndex: 'id', width: 60 },
                { title: 'Nome', dataIndex: 'name' },
                { title: 'Telefone', dataIndex: 'phone', width: 140 },
                { title: 'E-mail', dataIndex: 'email' },
                { title: 'Origem', dataIndex: 'auth_provider', width: 100,
                  render: v => v === 'google' ? <Tag color="blue">Google</Tag> : <Tag>{v || 'form'}</Tag> },
                { title: 'Quando', dataIndex: 'created_at', width: 160, render: fmtDate },
              ]}
            />
          </Card>

          <Text type="secondary" style={{ display: 'block', marginTop: 12, textAlign: 'right', fontSize: 12 }}>
            Gerado em {dayjs(data.generated_at).format('DD/MM/YYYY HH:mm:ss')} · {data.took_ms}ms
          </Text>
        </>
      )}
    </div>
  );
};

export default StatsAdoption;
